import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { UserServable } from '@nida-web/api/generic-interfaces/user-management';
import notify from 'devextreme/ui/notify';
import { TranslocoService } from '@jsverse/transloco';
import { DxValidationGroupComponent } from 'devextreme-angular';
import { PasswordResetService, SessionManagerService } from '@nida-web/api/rest/authentication';
import { ActivatedRoute, Router } from '@angular/router';
import { PasswordAdapterService } from '@nida-web/api/rest/nidaserver/password3';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { APIPersonal, PersonalRESTService } from '@nida-web/api/rest/user-management';
import { StartPageRoutingService } from '@nida-web/core';
import { take, takeUntil } from 'rxjs/operators';
import { PasswordValidationService } from '@nida-web/shared/utils';
import { DxTextBoxTypes } from 'devextreme-angular/ui/text-box';
import { DxButtonTypes } from 'devextreme-angular/ui/button';
import {
  AsyncRule,
  CompareRule,
  CustomRule,
  EmailRule,
  NumericRule,
  PatternRule,
  RangeRule,
  RequiredRule,
  StringLengthRule,
} from 'devextreme/common';

@Component({
  selector: 'nida-web-password-management',
  templateUrl: './password-management.component.html',
  styleUrls: ['./password-management.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class PasswordManagementComponent implements OnInit, OnDestroy {
  @ViewChild('passwordChange', { static: false }) passwordChangeValidationGroup: DxValidationGroupComponent;
  @Input() headerName: string;
  @Input() buttonName: string;
  @Input() requireCurrentPassword: boolean;
  @Input() redirect: boolean;
  @Input() nidaMobile: boolean;
  @Input() requireUserName: boolean;
  passwordChangeForm: UntypedFormGroup;
  passwordMode: DxTextBoxTypes.TextBoxType = 'password';
  passwordButton: DxButtonTypes.Properties;
  user: APIPersonal;
  public passwordValidationRules: Array<
    RequiredRule | NumericRule | RangeRule | StringLengthRule | CustomRule | CompareRule | PatternRule | EmailRule | AsyncRule
  >;
  private readonly unsubscribe$: Subject<void> = new Subject();
  private userId: number;
  private userName: string;
  private sessionSub: Subscription;
  private sessionSub2: Subscription;
  private passwordResetToken?: string;

  constructor(
    private sessionManager: SessionManagerService,
    private passwordUserService: UserServable,
    private translocoService: TranslocoService,
    protected router: Router,
    private passwordNIDAmobileService: PasswordAdapterService,
    private personalRESTService: PersonalRESTService,
    private startPageRoutingService: StartPageRoutingService,
    private passwordValidationService: PasswordValidationService,
    private activatedRoute: ActivatedRoute,
    private passwordResetService: PasswordResetService
  ) {
    this.passwordValidationRules = this.passwordValidationService.getPasswordValidationRules();
    this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe((params) => {
      if (this.router.url.startsWith('/password-reset')) this.passwordResetToken = params.token;
    });
  }

  ngOnDestroy(): void {
    if (this.sessionSub) this.sessionSub.unsubscribe();
    if (this.sessionSub2) this.sessionSub2.unsubscribe();

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    this.initFormGroup();
    this.passwordButton = {
      icon: 'eyeopen',
      stylingMode: 'contained',
      type: 'default',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      },
    };

    this.sessionSub = this.sessionManager
      .getSessionInformation()
      .pipe(take(1))
      .subscribe((sessionInformation) => {
        if (sessionInformation.loggedIn) {
          if (sessionInformation.userId) {
            this.userId = sessionInformation.userId;
          } else {
            this.userId = -1;
          }
          if (sessionInformation.userName) {
            this.userName = sessionInformation.userName;
          }
          this.personalRESTService.getPersonById(this.userId.toString()).subscribe((resultUser) => {
            this.passwordChangeForm.patchValue({ userName: resultUser.benutzer });
            this.user = resultUser;
          });
        }
      });
  }

  trySaveNewPassword(): void {
    /**
     * Check if Password is set for NIDAmobile or the User
     */
    if (this.nidaMobile) {
      this.changeNIDAmobilePassword();
      /**
       user has forgotten password and requested password reset link by mail
       */
    } else if (this.passwordResetToken) {
      this.passwordResetService
        .resetPasswordWithToken({
          token: this.passwordResetToken,
          newPassword: this.passwordChangeForm.getRawValue().newPassword,
        })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (success) => {
            if (success) {
              notify({
                message: this.translocoService.translate('saveSuccessfully'),
                type: 'Success',
                displayTime: 6000,
              });
              this.passwordChangeForm.reset();
              this.passwordChangeValidationGroup.instance.reset();
              if (this.redirect) {
                this.router.navigate(['login']);
              }
            }
          },
          error: () => {
            notify({
              message: this.translocoService.translate('unknownServerError'),
              type: 'warning',
              displayTime: 5000,
            });
          },
        });
      /**
       * password was sent to user directly via e-mail
       */
    } else {
      this.passwordUserService
        .changePassword(this.userId, this.passwordChangeForm.getRawValue().oldPassword, this.passwordChangeForm.getRawValue().newPassword)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (success) => {
            if (success) {
              notify({
                message: this.translocoService.translate('saveSuccessfully'),
                type: 'Success',
                displayTime: 6000,
              });
              this.passwordChangeForm.reset();
              this.passwordChangeValidationGroup.instance.reset();
              if (this.redirect) {
                this.navigateHome();
              }
            }
          },
          error: () => {
            notify({
              message: this.translocoService.translate('unknownServerError'),
              type: 'warning',
              displayTime: 5000,
            });
          },
        });
    }
  }

  public passwordComparison = () => {
    return this.passwordChangeForm.getRawValue().newPassword;
  };

  navigateHome() {
    this.startPageRoutingService.calculateRoute();
  }

  changeNIDAmobilePassword() {
    this.sessionSub2 = this.sessionManager
      .getSessionInformation()
      .pipe(take(1))
      .subscribe((sessionInformation) => {
        if (sessionInformation.loggedIn) {
          if (sessionInformation.userId) {
            this.userId = sessionInformation.userId;
          } else {
            this.userId = -1;
          }

          this.passwordNIDAmobileService
            .changeUserMobilePassword({ newPassword: this.passwordChangeForm.getRawValue().newPassword }, this.userId)
            .subscribe(() => {
              notify({
                message: this.translocoService.translate('saveSuccessfully'),
                type: 'Success',
                displayTime: 6000,
              });

              this.userName = this.passwordChangeForm.getRawValue().userName;
              if (this.userName.length > 1) {
                this.user.benutzer = this.userName;
                this.updateUser(this.user, this.userId);
              }

              this.passwordChangeForm.reset();
              this.passwordChangeValidationGroup.instance.reset();

              this.navigateHome();
            });
        }
      });
  }

  public updateUser(user: APIPersonal, id: number): Observable<number> {
    const resultSubject = new ReplaySubject<number>(1);

    this.personalRESTService.savePerson(user, id).subscribe((resultUser) => {
      if (resultUser === undefined || resultUser.id === undefined) {
        resultSubject.next(-1);
      } else if (resultUser.id >= 0) {
        resultSubject.next(resultUser.id);
      } else {
        resultSubject.next(-1);
      }
    });

    return resultSubject.asObservable();
  }

  private initFormGroup() {
    this.passwordChangeForm = new UntypedFormGroup({
      userName: new UntypedFormControl(null),
      oldPassword: new UntypedFormControl(null),
      newPassword: new UntypedFormControl(null),
      passwordReplay: new UntypedFormControl(null),
    });
  }
}
